import cn from 'classnames'
import { AnimationProps, motion } from 'framer-motion'
import { observer } from 'mobx-react'
import React, { useId, useState } from 'react'

import {
   AsyncComboBox,
   colors,
   ComboboxContextProvider,
   CustomOptionType,
   Item
} from '@flowwai-frontend/design-system'
import {
   Delete02Icon,
   MapPin02Icon,
   SearchMdIcon,
   XCloseIcon
} from '@flowwai-frontend/design-system/icons'
import {
   EMPTY_STRING,
   ENTER_KEY_VALUE
} from '@flowwai-frontend/shared/constants'
import {
   validateLatitude,
   validateLongitude
} from '@flowwai-frontend/shared/sales-crm/sales-crm-core'

import {
   API_CONTENT_TYPE,
   API_METHOD,
   MAP_CENTER_POINT,
   RADIUS_IN_METERS
} from '../../../constants/OlaMapsAutocompleteConstants'
import { useMapsUtilityContext } from '../../../hooks/useMapsUtilityContext'
import useGooglePlacesSearch, {
   GooglePlaceItem
} from '../../../hooks/useGooglePlacesSearch'
import { MapCoordinates } from '../../../types'
import { getOlaMapSearchUrl } from '../../../utils/OlaMapsAutocompleteUtil'

import * as Styles from './styles'

interface ResponseData {
   mainText: string
   secondaryText: string
   location: {
      lng: number
      lat: number
   }
   referenceId: string
}

interface PlaceOptionProps {
   mainText: string
   secondaryText?: string
   optionProps: React.HTMLAttributes<HTMLDivElement>
   optionRef: React.Ref<HTMLDivElement>
}

interface Props {
   onSearchLocation: (coordinates: MapCoordinates) => void
   className?: string
   alwaysExpanded?: boolean
   animationProps?: AnimationProps
}

const PlaceOption = (props: PlaceOptionProps): React.ReactElement => {
   const { mainText, secondaryText, optionProps, optionRef } = props

   const optionText = secondaryText ? `${mainText}, ${secondaryText}` : mainText

   return (
      <div
         className={cn(Styles.placesSearchCustomOption)}
         ref={optionRef}
         {...optionProps}
      >
         <MapPin02Icon height={20} width={20} />
         <p className={cn(Styles.placesSearchOptionText)} title={optionText}>
            {optionText}
         </p>
      </div>
   )
}

const PlacesSearchTool = (props: Props): React.ReactElement => {
   const [isExpanded, setIsExpanded] = useState(props.alwaysExpanded ?? false)
   const [inputValue, setInputValue] = useState('')
   const [responseData, setResponseData] = useState<ResponseData[]>([])

   const {
      mapsT,
      serviceProvidersConfig: { search: searchServiceProviderConfig }
   } = useMapsUtilityContext()

   const requestId = useId()

   const {
      fetchSuggestions: fetchGoogleSuggestions,
      onSelectPlace
   } = useGooglePlacesSearch({
      onLocationSelect: props.onSearchLocation,
      onInputChange: setInputValue
   })

   const olaMapSearchApi = async (
      page: number,
      filterText?: string
   ): Promise<Array<ResponseData>> => {
      if (!filterText?.length) return []
      try {
         const url = getOlaMapSearchUrl(
            MAP_CENTER_POINT.latitude,
            MAP_CENTER_POINT.longitude,
            RADIUS_IN_METERS,
            filterText
         )
         const response = await fetch(url, {
            method: API_METHOD,
            headers: {
               'Content-Type': API_CONTENT_TYPE,
               'X-Request-Id': requestId
            }
         })

         if (response.ok) {
            const data = await response.json()
            const results = data.predictions.map((each: any) => ({
               mainText: each.structured_formatting.main_text,
               secondaryText: each.structured_formatting.secondary_text,
               location: each.geometry.location,
               referenceId: each.reference
            }))

            setResponseData(results)
            return results
         }
         return []
      } catch (error) {
         return []
      }
   }

   const renderCustomOption = (renderProps: CustomOptionType<ResponseData>) => {
      const { item, optionProps, optionRef } = renderProps
      const { value } = item

      return (
         <PlaceOption
            mainText={value.mainText}
            secondaryText={value.secondaryText}
            optionProps={optionProps}
            optionRef={optionRef}
         />
      )
   }

   const renderGoogleCustomOption = (
      renderProps: CustomOptionType<GooglePlaceItem>
   ) => {
      const { item, optionProps, optionRef } = renderProps
      const { value } = item

      return (
         <PlaceOption
            mainText={value.mainText}
            secondaryText={value.secondaryText}
            optionProps={optionProps}
            optionRef={optionRef}
         />
      )
   }

   const onSelectionChange = (key: React.Key) => {
      const selectedData = responseData.find(data => data.referenceId === key)
      if (selectedData) {
         setInputValue(selectedData.mainText)
         props.onSearchLocation(selectedData.location)
      }
   }

   const testCoordinatesInputAndAddCoordinates = (
      inputString: string
   ): boolean => {
      const [latString, lngString] = inputString.split(',')

      const trimmedLatString = latString?.trim()

      const latitudeValue = trimmedLatString ? Number(trimmedLatString) : null

      const isValidLatitude = latitudeValue
         ? validateLatitude(latitudeValue)
         : false

      const trimmedLngString = lngString?.trim()

      const longitudeValue = trimmedLngString ? Number(trimmedLngString) : null

      const isValidLongitude = longitudeValue
         ? validateLongitude(longitudeValue)
         : false

      const isValidLatAndLng = isValidLatitude && isValidLongitude
      if (!isValidLatAndLng) return false

      props.onSearchLocation({ lat: latitudeValue!, lng: longitudeValue! })
      return true
   }

   const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
      const isEnterKey = e.key === ENTER_KEY_VALUE

      if (!isEnterKey) return

      testCoordinatesInputAndAddCoordinates(e.currentTarget.value)
   }

   const renderClearIcon = (): React.ReactNode =>
      inputValue ? (
         <button onClick={() => setInputValue(EMPTY_STRING)}>
            <Delete02Icon />
         </button>
      ) : null

   const renderGoogleMapsCombobox = (): React.ReactElement => (
      <ComboboxContextProvider>
         <AsyncComboBox
            placeholder={mapsT('olaMapSearchInput.placeholder')}
            apiProps={{
               apiFunction: fetchGoogleSuggestions,
               isInfiniteScrollable: true
            }}
            size={'ExtraSmall'}
            customOption={renderGoogleCustomOption}
            onSelectionChange={onSelectPlace}
            inputValue={inputValue}
            onInputChange={setInputValue}
            popoverStyles={{
               padding: '10px',
               width: '360px',
               left: '24px',
               top: '64px'
            }}
            alwaysShowWebView={true}
            onKeyDown={onKeyDown}
            prefixEnhancer={() => <></>}
            className='!border-none'
            inputClassName='!p-0 -ml-[6px]'
            focusRingVariant='None'
            hideChevronIcon
            shouldRenderClearIcon={false}
            autoFocus={!props.alwaysExpanded}
            menuTrigger={inputValue ? 'focus' : 'input'}
         >
            {option => (
               <Item textValue={option.mainText} key={option.placeId}>
                  {option.mainText}
               </Item>
            )}
         </AsyncComboBox>
      </ComboboxContextProvider>
   )

   const renderPlacesSearchCombobox = (): React.ReactElement => {
      switch (searchServiceProviderConfig?.serviceProvider) {
         case 'GOOGLE_MAPS':
            return renderGoogleMapsCombobox()
         case 'OLA_MAPS': {
            return (
               <ComboboxContextProvider>
                  <AsyncComboBox
                     placeholder={mapsT('olaMapSearchInput.placeholder')}
                     apiProps={{
                        apiFunction: olaMapSearchApi,
                        isInfiniteScrollable: true
                     }}
                     size={'ExtraSmall'}
                     customOption={renderCustomOption}
                     onSelectionChange={onSelectionChange}
                     inputValue={inputValue}
                     onInputChange={setInputValue}
                     popoverStyles={{
                        padding: '10px',
                        width: '360px',
                        left: '24px',
                        top: '64px'
                     }}
                     alwaysShowWebView={true}
                     onKeyDown={onKeyDown}
                     prefixEnhancer={() => <></>}
                     className='!border-none'
                     inputClassName='!p-0 -ml-[6px]'
                     focusRingVariant='None'
                     hideChevronIcon
                     shouldRenderClearIcon={false}
                     autoFocus={!props.alwaysExpanded}
                     menuTrigger={inputValue ? 'focus' : 'input'}
                  >
                     {option => (
                        <Item
                           textValue={option.mainText as string}
                           key={option.referenceId}
                        >
                           {option.mainText}
                        </Item>
                     )}
                  </AsyncComboBox>
               </ComboboxContextProvider>
            )
         }
         default:
            return <></>
      }
   }

   const onToggleExpand = () => {
      if (props.alwaysExpanded) return
      setIsExpanded(prev => !prev)
   }

   return (
      <motion.div
         className={cn(
            'bg-base-white rounded-full flex items-center p-3 shadow-lg',
            props.className,
            isExpanded ? 'w-[360px]' : 'cursor-pointer'
         )}
         {...props.animationProps}
         onClick={isExpanded ? undefined : onToggleExpand}
      >
         <button
            onClick={e => {
               e.stopPropagation()
               onToggleExpand()
            }}
         >
            <SearchMdIcon height={20} width={20} fill={colors['gray-800']} />
         </button>

         {isExpanded ? (
            <>
               {renderPlacesSearchCombobox()}

               {renderClearIcon()}

               {props.alwaysExpanded ? null : (
                  <>
                     <div className='h-[14px] w-px bg-gray-300 mx-2' />
                     <button
                        className='pr-1'
                        onClick={() => setIsExpanded(prev => !prev)}
                     >
                        <XCloseIcon
                           height={16}
                           width={16}
                           fill={colors['gray-500']}
                        />
                     </button>
                  </>
               )}
            </>
         ) : null}
      </motion.div>
   )
}

export default observer(PlacesSearchTool)
